import { Injectable, Injector, inject } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { NavigationEnd, Router, TitleStrategy } from '@angular/router';
import { FronteggAuthService } from '@frontegg/angular';
import posthog from 'posthog-js';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ShopService } from '@services/shops/shop.service';
import { environment } from '@environment/environment';
import { AnalyticsEventMap, AnalyticsEventName } from '../analytics/analytics.events';
import { AnalyticsService } from '../analytics/analytics.service';
import { FeatureFlag } from '../feature-flags/feature-flag.model';
import { FeatureFlagService } from '../feature-flags/feature-flag.service';
import { PosthogClient, PosthogTracker, PosthogUser } from './posthog-tracker';
import { buildRoutePattern } from './route-pattern';

interface FronteggRoleLike {
  key?: string;
  name?: string;
}

interface FronteggUserLike {
  id: string;
  email: string;
  name?: string;
  tenantId: string;
  roles?: FronteggRoleLike[];
}

@Injectable({ providedIn: 'root' })
export class PosthogService implements AnalyticsService, FeatureFlagService {
  private readonly injector = inject(Injector);
  private readonly tracker = new PosthogTracker(posthog as unknown as PosthogClient, { env: environment.env });
  private started = false;

  get currentRoutePattern(): string {
    return this.tracker.currentRoutePattern;
  }

  init(): void {
    if (this.started) return;
    this.started = true;
    this.tracker.init(environment.POSTHOG_API_KEY, environment.POSTHOG_HOST);
    if (!this.tracker.enabled) return;

    this.injector
      .get(FronteggAuthService)
      .user$.subscribe((user) => this.tracker.setUser(user ? this.toPosthogUser(user as FronteggUserLike) : null));

    this.injector
      .get(ShopService)
      .shop$.pipe(filter(Boolean))
      .subscribe((shop) => this.tracker.setShop({ id: shop.id, name: shop.name }));

    const router = this.injector.get(Router);
    const titleStrategy = this.injector.get(TitleStrategy);
    router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
      const snapshot = router.routerState.snapshot;
      this.tracker.pageview({
        url: window.location.origin + event.urlAfterRedirects,
        routePattern: buildRoutePattern(snapshot.root),
        title: titleStrategy.buildTitle(snapshot) ?? '',
      });
    });
  }

  capture<N extends AnalyticsEventName>(name: N, props: AnalyticsEventMap[N]): void {
    this.tracker.capture(name, props);
  }

  isEnabled(flag: FeatureFlag): boolean {
    return this.tracker.isFeatureEnabled(flag);
  }

  isEnabled$(flag: FeatureFlag): Observable<boolean> {
    return this.tracker.isFeatureEnabled$(flag);
  }

  trackSave<T>(form: AbstractControl, openedAt: number, save$: Observable<T>): Observable<T> {
    return this.tracker.trackSave(form, openedAt, save$);
  }

  private toPosthogUser(user: FronteggUserLike): PosthogUser {
    return {
      id: user.id,
      email: user.email,
      name: user.name,
      shopId: String(user.tenantId),
      roles: (user.roles ?? []).map((role) => role.key ?? role.name ?? '').filter(Boolean),
    };
  }
}
